import {
  Button,
  Card,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconFileText,
  IconNotebook,
  IconShieldCheck,
} from "@tabler/icons-react";
import { Link } from "react-router-dom";
import "./AssignmentHelpHowItWorks.css";

export const assignmentHelpHowItWorksSteps = [
  {
    title: "Share Your Assignment Brief",
    description:
      "Send your assignment question, subject, word count, file instructions, and the part where you need help. Our assignment experts review your brief carefully so the support is focused on your exact academic task.",
  },
  {
    title: "Tell Us Your Deadline and Requirements",
    description:
      "Share your deadline, marking rubric, module guidelines, referencing style, and any university instructions. This helps our assignment help team guide you according to your submission expectations.",
  },
  {
    title: "Get Help from Assignment Experts",
    description:
      "Receive subject-specific assignment help from experts who can guide you with topic understanding, structure planning, research direction, calculations, coding support, case analysis, or draft improvement.",
  },
  {
    title: "Improve Your Final Assignment",
    description:
      "Use expert guidance to refine your outline, strengthen your arguments, improve clarity, fix weak sections, and prepare a better final submission before your deadline.",
  },
];

const assignmentHelpFlowSteps = [
  {
    title: "Submit Your Requirements",
    description:
      "Share your assignment details, deadline and specific support needs. Fill the Form.",
    icon: IconNotebook,
  },
  {
    title: "Confirm Your Order Details",
    description:
      "Our team reviews the request, discuss pricing with you, discusses the support needed, and confirms the order.",
    icon: IconShieldCheck,
  },
  {
    title: "Receive Your Order",
    description:
      "Get your completed work done by our experts and help delivered in time for your deadline.",
    icon: IconFileText,
  },
];

interface AssignmentHelpHowItWorksProps {
  className?: string;
  title?: string;
  variant?: "cards" | "flow";
}

function AssignmentHelpHowItWorks({
  className,
  title = "How SubjectBuddy Assignment Help Works",
  variant = "cards",
}: AssignmentHelpHowItWorksProps) {
  const sectionClassName = ["assignment-help-how-it-works", className]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={sectionClassName}
      aria-labelledby="assignment-help-how-it-works-heading"
    >
      <Stack gap="lg">
        <Title order={2} id="assignment-help-how-it-works-heading">
          {title}
        </Title>
        {variant === "flow" ? (
          <>
            <div className="assignment-help-how-it-works-flow">
              {assignmentHelpFlowSteps.map((item, index) => (
                <div
                  key={item.title}
                  className="assignment-help-how-it-works-flow-node"
                >
                  <div className="assignment-help-how-it-works-flow-item">
                    <div className="assignment-help-how-it-works-flow-icon-shell">
                      <item.icon size={56} stroke={1.5} />
                    </div>
                    <Title order={3}>{item.title}</Title>
                    <Text
                      c="dimmed"
                      className="assignment-help-how-it-works-flow-copy"
                    >
                      {item.description}
                    </Text>
                  </div>

                  {index < assignmentHelpFlowSteps.length - 1 ? (
                    <div
                      className="assignment-help-how-it-works-flow-arrow"
                      aria-hidden="true"
                    >
                      <IconArrowRight size={44} stroke={2.2} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>

            <div className="assignment-help-how-it-works-flow-cta">
              <Button
                component={Link}
                to="/order-form"
                color="green"
                radius="md"
                size="md"
              >
                Order Now
              </Button>
            </div>
          </>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="lg">
            {assignmentHelpHowItWorksSteps.map((item, index) => (
              <Card
                key={item.title}
                withBorder
                radius="md"
                p="lg"
                className="assignment-help-how-it-works-card"
              >
                <Stack gap="sm">
                  <ThemeIcon
                    color="green"
                    variant="light"
                    radius="xl"
                    size={42}
                    className="assignment-help-how-it-works-icon"
                  >
                    {index + 1}
                  </ThemeIcon>
                  <Title order={3}>{item.title}</Title>
                  <Text
                    size="sm"
                    c="dimmed"
                    className="assignment-help-how-it-works-copy"
                  >
                    {item.description}
                  </Text>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </Stack>
    </section>
  );
}

export default AssignmentHelpHowItWorks;
